import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatDateTime } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, ChannelBadge, StatusBadge, EmptyState, ConfirmDialog } from "../../components/common";
import { DEALER_STATUS, timeAgo, waLink } from "../../lib/channels";
import { Search, Phone, MessageCircle, Mail, Download, Trash2, UserPlus, Building2, MapPin, Wrench, Copy, Pause, Play, UserX } from "lucide-react";
import { toast } from "sonner";

const ORDER = ["all", "yeni", "inceleniyor", "gorusuldu", "onaylandi", "reddedildi"];

export default function Dealers() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [creds, setCreds] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind}
  const [acc, setAcc] = useState({ open: false, email: "", password: "", commission_percent: 30 });

  const load = useCallback(async () => {
    const { data } = await api.get("/dealers", { params: q ? { q } : {} });
    setList(data);
    setSel((cur) => (cur ? data.find((d) => d.id === cur.id) || null : cur));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: list.length };
    list.forEach((d) => { c[d.status] = (c[d.status] || 0) + 1; });
    return c;
  }, [list]);
  const shown = list.filter((d) => status === "all" || d.status === status);

  const pick = (d) => { setSel(d); setNote(d.notes || ""); };
  const update = async (patch, msg) => {
    const { data } = await api.put(`/dealers/${sel.id}`, patch);
    setSel(data);
    setList((l) => l.map((x) => (x.id === data.id ? data : x)));
    if (msg) toast.success(msg);
  };
  const remove = async () => {
    await api.delete(`/dealers/${sel.id}`);
    setConfirm(null); setSel(null); load();
    toast.success("Başvuru silindi");
  };
  const toggleTech = async () => {
    const active = !sel.technician_active;
    await api.put(`/technicians/${sel.technician_id}`, { is_active: active });
    toast.success(active ? "Usta tekrar aktif — giriş yapabilir" : "Usta durduruldu — giriş yapamaz");
    load();
  };
  const deleteTech = async (force) => {
    try {
      await api.delete(`/technicians/${sel.technician_id}`, { params: force ? { force: true } : {} });
      toast.success("Usta hesabı silindi"); setConfirm(null); load();
    } catch (e) {
      if (e.response?.status === 409 && !force) setConfirm({ kind: "tech-force", msg: e.response.data?.detail });
      else { toast.error(e.response?.data?.detail || "Silinemedi"); setConfirm(null); }
    }
  };
  const createAccount = async () => {
    try {
      const { data } = await api.post(`/dealers/${sel.id}/create-technician`, { email: acc.email, password: acc.password, commission_percent: Number(acc.commission_percent) });
      toast.success("Usta hesabı oluşturuldu; başvuru onaylandı");
      setCreds({ ...data, phone: sel.phone });
      setAcc({ open: false, email: "", password: "", commission_percent: 30 });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Hata: bilgileri kontrol edin (şifre en az 6 karakter)");
    }
  };
  const download = async () => {
    const res = await api.get("/export/dealers", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data); a.download = "bayilik-basvurulari.csv"; a.click();
  };

  return (
    <div className="space-y-6" data-testid="dealers-page">
      <PageHeader eyebrow="Web Formları" title="Bayilik Başvuruları" subtitle="“Servis İş Ortağımız Olun” formundan gelen başvurular. Değerlendirin, görüşün ve onayladığınızda tek tıkla teknisyen hesabı açın.">
        <Button variant="outline" onClick={download} data-testid="export-dealers"><Download className="h-4 w-4 mr-2" />CSV</Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {ORDER.slice(1).map((s) => (
          <button key={s} onClick={() => setStatus(status === s ? "all" : s)} data-testid={`dealer-stat-${s}`}
            className={`text-left p-4 rounded-xl border transition-colors ${status === s ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{DEALER_STATUS[s].label}</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-1">{counts[s] || 0}</div>
          </button>
        ))}
      </div>

      <Card className="p-4 border border-slate-200 shadow-none">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma, yetkili, telefon veya il ile ara..." className="pl-9" data-testid="dealer-search" />
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dealers-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Firma</th><th className="px-4 py-3">Yetkili</th><th className="px-4 py-3">Bölge</th><th className="px-4 py-3">Hizmet</th><th className="px-4 py-3">Kanal</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3">Tarih</th></tr>
            </thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={7}><EmptyState icon={Building2} title="Başvuru bulunamadı" hint="Sitedeki “Servis İş Ortağımız Olun” formundan gelen başvurular burada listelenir." /></td></tr>}
              {shown.map((d) => (
                <tr key={d.id} onClick={() => pick(d)} className="border-b border-slate-100 hover:bg-slate-50/70 cursor-pointer" data-testid={`dealer-row-${d.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{d.company}</td>
                  <td className="px-4 py-3"><div>{d.contact_name}</div><div className="text-xs text-slate-500">{d.phone}</div></td>
                  <td className="px-4 py-3 text-slate-600">{[d.district, d.city].filter(Boolean).join(" / ")}</td>
                  <td className="px-4 py-3 text-slate-600">{d.service_types || "-"}</td>
                  <td className="px-4 py-3"><ChannelBadge channel={d.channel} /></td>
                  <td className="px-4 py-3"><StatusBadge map={DEALER_STATUS} value={d.status} /></td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{timeAgo(d.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dealer-dialog">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-blue-600" />{sel.company}</DialogTitle></DialogHeader>
              <div className="flex items-center gap-2 flex-wrap text-sm text-slate-500">
                <StatusBadge map={DEALER_STATUS} value={sel.status} /> <span>{formatDateTime(sel.created_at)}</span> <ChannelBadge channel={sel.channel} />
              </div>
              <div className="flex flex-wrap gap-2">
                <a href={`tel:${sel.phone}`}><Button variant="outline" size="sm"><Phone className="h-4 w-4 mr-2" />{sel.phone}</Button></a>
                <a href={waLink(sel.phone, `Merhaba ${sel.contact_name}, ServisPlus iş ortaklığı başvurunuz hakkında yazıyorum.`)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</Button></a>
                {sel.email && <a href={`mailto:${sel.email}`}><Button variant="outline" size="sm"><Mail className="h-4 w-4 mr-2" />{sel.email}</Button></a>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-slate-50"><div className="text-[11px] uppercase tracking-wider text-slate-500">Yetkili</div><div className="font-medium mt-0.5">{sel.contact_name}</div></div>
                <div className="p-3 rounded-lg bg-slate-50"><div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />Hizmet bölgesi</div><div className="font-medium mt-0.5">{sel.city}{sel.district ? ` — ${sel.district}` : ""}</div></div>
                <div className="p-3 rounded-lg bg-slate-50 col-span-2"><div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Wrench className="h-3 w-3" />Hizmet türü</div><div className="font-medium mt-0.5">{sel.service_types || "-"}</div></div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Başvuru notu (ekip, araç, tecrübe)</div>
                <div className="p-4 rounded-lg border border-slate-200 whitespace-pre-wrap min-h-[60px]">{sel.message || <span className="text-slate-400">Not yazılmamış</span>}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Durum</Label>
                  <Select value={sel.status} onValueChange={(v) => update({ status: v }, "Durum güncellendi")}>
                    <SelectTrigger data-testid="dealer-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(DEALER_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  {sel.technician_id ? (
                    <div className="w-full space-y-2">
                      <div className={`text-sm p-2.5 rounded-md border ${sel.technician_active ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                        {sel.technician_active ? "Usta hesabı aktif ✓" : "Usta durduruldu (giriş yapamaz)"}
                        {sel.technician_email && <div className="text-xs opacity-80 mt-0.5">{sel.technician_email}</div>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1" onClick={toggleTech} data-testid="dealer-toggle-tech">
                          {sel.technician_active ? <><Pause className="h-3.5 w-3.5 mr-1" />Durdur</> : <><Play className="h-3.5 w-3.5 mr-1" />Başlat</>}
                        </Button>
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setConfirm({ kind: "tech" })} data-testid="dealer-delete-tech"><UserX className="h-3.5 w-3.5 mr-1" />Ustayı sil</Button>
                      </div>
                    </div>
                  ) : (
                    <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => setAcc({ ...acc, open: true, email: sel.email || "" })} data-testid="dealer-create-account"><UserPlus className="h-4 w-4 mr-2" />Teknisyen Hesabı Aç</Button>
                  )}
                </div>
              </div>
              <div>
                <Label>İç not</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="dealer-note" />
              </div>
              <DialogFooter className="sm:justify-between">
                <Button variant="ghost" className="text-rose-600" onClick={() => setConfirm({ kind: "dealer" })} data-testid="dealer-delete"><Trash2 className="h-4 w-4 mr-1.5" />Sil</Button>
                <Button variant="outline" onClick={() => update({ note }, "Not kaydedildi")} data-testid="dealer-save-note">Notu Kaydet</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={acc.open} onOpenChange={(o) => setAcc({ ...acc, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Teknisyen Hesabı Oluştur</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Bu firma için teknisyen paneli giriş bilgisi oluşturulur ve başvuru “Onaylandı” olarak işaretlenir.</p>
          <div className="space-y-3">
            <div><Label>E-posta (giriş)</Label><Input placeholder="boşsa otomatik oluşturulur" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} data-testid="acc-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Geçici şifre</Label><Input type="text" placeholder="boşsa otomatik" value={acc.password} onChange={(e) => setAcc({ ...acc, password: e.target.value })} data-testid="acc-password" /></div>
              <div><Label>Şirkete borç oranı %</Label><Input type="number" value={acc.commission_percent} onChange={(e) => setAcc({ ...acc, commission_percent: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcc({ ...acc, open: false })}>Vazgeç</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={createAccount} data-testid="acc-confirm">Hesabı Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent data-testid="creds-dialog">
          <DialogHeader><DialogTitle>Usta hesabı hazır ✓</DialogTitle></DialogHeader>
          {creds && (() => {
            const loginUrl = `${window.location.origin}/login`;
            const text = `Merhaba, ServisPlus usta paneli giriş bilgileriniz:\nAdres: ${loginUrl}\nE-posta: ${creds.email}\nŞifre: ${creds.password}`;
            return (
              <>
                <p className="text-sm text-slate-500">Başvuru onaylandı. Bu bilgileri ustaya iletin; şifre bir daha gösterilmez.</p>
                <pre className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm whitespace-pre-wrap" data-testid="creds-text">{text}</pre>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(text); toast.success("Kopyalandı"); }}><Copy className="h-4 w-4 mr-1.5" />Kopyala</Button>
                  <a href={waLink(creds.phone, text)} target="_blank" rel="noreferrer"><Button className="bg-emerald-600 hover:bg-emerald-700"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp ile gönder</Button></a>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.kind === "dealer" ? "Başvuru silinsin mi?" : confirm?.kind === "tech-force" ? "Yine de silinsin mi?" : "Usta hesabı silinsin mi?"}
        description={confirm?.kind === "dealer" ? "Başvuru kalıcı olarak silinir." : confirm?.kind === "tech-force" ? `${confirm.msg || ""}` : "Usta bir daha giriş yapamaz. Geçici engellemek için “Durdur”u kullanın."}
        onConfirm={() => confirm.kind === "dealer" ? remove() : deleteTech(confirm.kind === "tech-force")}
      />
    </div>
  );
}
